import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

import TikTokIcon from "@/assets/tiktok";
import { isTikTokFeatureOn, useTikTokAccess } from "@/hooks/useTikTokAccess";
import { cn } from "@/lib/utils";
import { COMING_SOON_HINT, ComingSoonBadge } from "../tiktok/pre-release";

import AiSearch from "./AiSearch";
import { TikTokCreatorSearch } from "./creators/TikTokCreatorSearch";

type Mode = "posts" | "creators";

const readMode = (): Mode => (window.location.hash === "#creators" ? "creators" : "posts");

function ModeSwitch({
  mode,
  onChange,
  creatorsComingSoon,
}: {
  mode: Mode;
  onChange: (mode: Mode) => void;
  /** PRE_RELEASE_TIKTOK: the creators mode is shown but waits on TikTok. */
  creatorsComingSoon: boolean;
}) {
  const options: Array<{ value: Mode; label: string; icon: React.ReactNode; disabled?: boolean }> = [
    { value: "posts", label: "Social posts", icon: <Sparkles className="h-3.5 w-3.5" /> },
    {
      value: "creators",
      label: "TikTok creators",
      icon: <TikTokIcon className="h-3.5 w-3.5" />,
      disabled: creatorsComingSoon,
    },
  ];
  return (
    <div role="tablist" aria-label="Search mode" className="inline-flex rounded-lg border border-border bg-muted p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={mode === option.value}
          aria-disabled={option.disabled || undefined}
          disabled={option.disabled}
          title={option.disabled ? COMING_SOON_HINT : undefined}
          onClick={() => onChange(option.value)}
          className={cn(
            "relative inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
            option.disabled
              ? "cursor-not-allowed text-muted-foreground/60"
              : mode === option.value
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.icon}
          {option.label}
          {option.disabled && <ComingSoonBadge floating size="sm" className="-right-1.5 -top-2.5" />}
        </button>
      ))}
    </div>
  );
}

/**
 * Social Search with two modes: natural-language post search (Sociable,
 * paid per search) and TikTok Shop creator search (TikTok's free API, rate
 * limited). The mode lives in the URL hash so a reload keeps it.
 */
export default function SocialSearch() {
  const access = useTikTokAccess();
  // The switch exists for brands with access to the module; the creators mode
  // itself opens once TikTok approves creator search (PRE_RELEASE_TIKTOK).
  const creatorsAvailable = access.enabled;
  const creatorsOn = isTikTokFeatureOn(access.features, "creatorSearch");
  const [mode, setMode] = useState<Mode>(readMode);

  useEffect(() => {
    const onHash = () => setMode(readMode());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const changeMode = (next: Mode) => {
    setMode(next);
    window.history.replaceState(null, "", next === "creators" ? "#creators" : window.location.pathname);
  };

  if (!creatorsAvailable) return <AiSearch />;

  const modeSwitch = <ModeSwitch mode={mode} onChange={changeMode} creatorsComingSoon={!creatorsOn} />;

  if (mode === "posts" || !creatorsOn) return <AiSearch modeSwitch={modeSwitch} />;

  return (
    <TikTokCreatorSearch
      header={
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100">
              <Sparkles className="h-5 w-5 text-brand-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">Social Search</h1>
              <p className="hidden text-sm text-muted-foreground sm:block">
                Find TikTok Shop creators to collaborate with.
              </p>
            </div>
          </div>
          {modeSwitch}
        </div>
      }
    />
  );
}
